import { inject, Injectable } from '@angular/core';
import { combineLatest, filter, map, Observable, switchMap } from 'rxjs';

import { CurrentConfigurationsService } from './current-configuration.service';
import { CurrentProjectService } from './current-project.service';
import { PermissionsService } from './permissions.service';
import { NavbarRoute } from '../components/navbar/navbar.component';
import { ProjectDto } from '@api/backend-connector/models';

const buildGlobalRoutes = (
  flags: CanViewFlags,
  routes: {
    dashboard?: string;
    admin?: string;
    superAdmin?: string;
    calendar?: string;
    teamAllocation?: string;
    inventory?: string;
    personalArea?: string;
    files?: string;
    budgetOverview?: string;
    milestones?: string;
    workload?: string;
  },
) => {
  return [
    // routes.dashboard !== undefined
    //   ? {
    //     routerLink: routes.dashboard,
    //     label: 'i18n.Routes.Label.dashboard',
    //     openInNew: false,
    //   }
    //   : undefined,
    flags.canViewAdminSettings && routes.admin !== undefined
      ? {
        routerLink: routes.admin,
        label: 'i18n.Routes.Label.settings',
        openInNew: false,
      }
      : undefined,
    flags.canViewSuperAdminSettings && routes.admin !== undefined
      ? {
        routerLink: routes.superAdmin,
        label: 'i18n.Routes.Label.superAdminSettings',
        openInNew: false,
      }
      : undefined,
    flags.canViewBudgetOverview && routes.budgetOverview !== undefined
      ? {
        routerLink: routes.budgetOverview,
        label: 'i18n.Routes.Label.budgetOverview',
        openInNew: false,
      }
      : undefined,
    flags.canViewMilestones && routes.milestones !== undefined
      ? {
        routerLink: routes.milestones,
        label: 'i18n.Routes.Label.milestones',
        openInNew: false,
      }
      : undefined,
    flags.canViewInventory && routes.inventory !== undefined
      ? {
        routerLink: routes.inventory,
        label: 'i18n.Routes.Label.inventory',
        openInNew: false,
      }
      : undefined,
    flags.canViewTeamAllocation && routes.teamAllocation !== undefined
      ? {
        routerLink: routes.teamAllocation,
        label: 'i18n.Routes.Label.teamAllocation',
        openInNew: false,
      }
      : undefined,
    flags.canViewWorkload && routes.workload !== undefined
      ? {
        routerLink: routes.workload,
        label: 'i18n.Routes.Label.workload',
        openInNew: false,
      }
      : undefined,
    flags.canViewCalendar && routes.calendar !== undefined
      ? {
        routerLink: routes.calendar,
        label: 'i18n.Routes.Label.calendar',
        openInNew: false,
      }
      : undefined,
    flags.canViewFiles && routes.files !== undefined
      ? {
        routerLink: routes.files,
        label: 'i18n.Routes.Label.files',
        openInNew: false,
      }
      : undefined,
  ].filter((r): r is NavbarRoute => !!r);
};

interface CanViewFlags {
  canViewAdminSettings: boolean;
  canViewSuperAdminSettings: boolean;
  canViewBudgetOverview: boolean;
  canViewMilestones: boolean;
  canViewTeamAllocation: boolean;
  canViewCalendar: boolean;
  canViewInventory: boolean;
  canViewFiles: boolean;
  canViewWorkload: boolean;
}

@Injectable({ providedIn: 'root' })
export class TopbarService {
  readonly permissionsService = inject(PermissionsService);
  readonly currentProjectService = inject(CurrentProjectService);
  readonly currentConfigurationsService = inject(CurrentConfigurationsService);

  readonly canView$: Observable<CanViewFlags> = combineLatest([
    this.currentConfigurationsService.budgetModuleEnabled(),
    this.currentConfigurationsService.milestonesEnabled(),
    this.currentConfigurationsService.inventoryEnabled(),
    this.currentConfigurationsService.filesEnabled(),
    this.permissionsService.canViewAdminSettings(),
    this.permissionsService.canViewTeamAllocation(),
    this.permissionsService.canViewCalendar(),
    this.permissionsService.isSuperAdmin(),
    this.permissionsService.isPlatformAdmin(),
  ]).pipe(
    map(
      ([
        budgetModuleEnabled,
        milestonesEnabled,
        inventoryEnabled,
        filesEnabled,
        canViewAdminSettings,
        canViewTeamAllocation,
        canViewCalendar,
        isSuperAdmin,
        isPlatformAdmin,
      ]) => ({
        canViewAdminSettings: !isSuperAdmin && canViewAdminSettings,
        canViewSuperAdminSettings: isSuperAdmin,
        // The budget overview goes with the budget module, as the team allocation and calendar do
        canViewBudgetOverview: budgetModuleEnabled && !isSuperAdmin && canViewAdminSettings,
        canViewMilestones: milestonesEnabled,
        canViewTeamAllocation: budgetModuleEnabled && canViewTeamAllocation,
        canViewCalendar: budgetModuleEnabled && canViewCalendar,
        canViewInventory: inventoryEnabled,
        canViewFiles: filesEnabled,
        canViewWorkload: budgetModuleEnabled && isPlatformAdmin && !isSuperAdmin,
      }),
    ),
  );

  readonly calendarRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: '../super-admin',
        inventory: '../inventory',
        teamAllocation: '../team-allocation',
        workload: '../workload',
        calendar: './',
        files: '/files',
        budgetOverview: '../budget-overview',
        milestones: '../milestones',
      });
    }),
  );

  readonly homeRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '',
        admin: '/settings',
        superAdmin: '/super-admin',
        inventory: '/inventory',
        teamAllocation: '/team-allocation',
        workload: '/workload',
        calendar: '/calendar',
        files: '/files',
        budgetOverview: '/budget-overview',
        milestones: '/milestones',
      });
    }),
  );

  readonly adminRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: './',
        superAdmin: '../super-admin',
        inventory: '../inventory',
        teamAllocation: '../team-allocation',
        workload: '../workload',
        calendar: '../calendar',
        files: '../files',
        budgetOverview: '../budget-overview',
        milestones: '../milestones',
      });
    }),
  );

  readonly superAdminRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: './',
        inventory: '../inventory',
        teamAllocation: '../team-allocation',
        workload: '../workload',
        calendar: '../calendar',
        files: '../files',
        budgetOverview: '../budget-overview',
        milestones: '../milestones',
      });
    }),
  );

  readonly inventoryRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: '../super-admin',
        inventory: './',
        teamAllocation: '../team-allocation',
        workload: '../workload',
        calendar: '../calendar',
        files: '../files',
        budgetOverview: '../budget-overview',
        milestones: '../milestones',
      });
    }),
  );

  readonly personalAreaRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: '../super-admin',
        inventory: '../inventory',
        teamAllocation: '../team-allocation',
        workload: '../workload',
        calendar: '../calendar',
        files: '../files',
        budgetOverview: '../budget-overview',
        milestones: '../milestones',
      });
    }),
  );

  readonly teamAllocationRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: '../super-admin',
        inventory: '../inventory',
        teamAllocation: './',
        workload: '../workload',
        calendar: '../calendar',
        files: '../files',
        budgetOverview: '../budget-overview',
        milestones: '../milestones',
      });
    }),
  );

  readonly workloadRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: '../super-admin',
        inventory: '../inventory',
        teamAllocation: '../team-allocation',
        workload: './',
        calendar: '../calendar',
        files: '../files',
        budgetOverview: '../budget-overview',
        milestones: '../milestones',
      });
    }),
  );

  readonly budgetOverviewRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: '../super-admin',
        inventory: '../inventory',
        teamAllocation: '../team-allocation',
        workload: '../workload',
        calendar: '../calendar',
        files: '../files',
        budgetOverview: './',
        milestones: '../milestones',
      });
    }),
  );

  readonly milestonesRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: '../super-admin',
        inventory: '../inventory',
        teamAllocation: '../team-allocation',
        workload: '../workload',
        calendar: '../calendar',
        files: '../files',
        budgetOverview: '../budget-overview',
        milestones: './',
      });
    }),
  );

  readonly filesRoutes$: Observable<NavbarRoute[]> = this.canView$.pipe(
    map((flags) => {
      return buildGlobalRoutes(flags, {
        dashboard: '/projects-resume',
        admin: '../settings',
        superAdmin: '../super-admin',
        inventory: '../inventory',
        teamAllocation: '../team-allocation',
        workload: '../workload',
        calendar: '../calendar',
        files: './',
        budgetOverview: '../budget-overview',
        milestones: '../milestones',
      });
    }),
  );

  readonly currentProject$ = this.currentProjectService.currentProject$.pipe(
    filter((project): project is ProjectDto => !!project),
  );

  readonly projectRoutes$: Observable<NavbarRoute[]> = this.currentProject$.pipe(
    switchMap((project) => {
      return combineLatest([this.permissionsService.canViewBudgetsTab(project.id)]).pipe(
        map(([canViewBudget]) => {
          const routes = [
            // {
            //   routerLink: 'dashboard',
            //   label: 'i18n.Routes.Label.dashboard',
            //   openInNew: false,
            // },
            {
              routerLink: 'tasks',
              label: 'i18n.Routes.Label.task',
              openInNew: false,
            },
            {
              routerLink: 'settings',
              label: 'i18n.Routes.Label.projectSettings',
              openInNew: false,
            },
          ];
          if (project.budgetEnabled && project.budgetsCount > 0 && canViewBudget) {
            routes.push({
              routerLink: 'budget',
              label: 'Budget',
              openInNew: false,
            });
          }
          return routes.filter((s) => s);
        }),
      );
    }),
  );
}
